import React, { useEffect, useState, memo } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Plus, Filter, Download, Eye, Edit, Search, Wrench, History, CheckCircle2, X, Save, Mail, Calendar } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
const initialEquipmentData = Array.from({
  length: 50
}, (_, i) => ({
  id: String(i + 1),
  code: `EQP-${String(i + 1).padStart(3, '0')}`,
  name: i % 2 === 0 ? `Fonometro ${i}` : `Stampante ${i}`,
  type: i % 4 === 0 ? 'Strumento Misura' : i % 4 === 1 ? 'Hardware' : i % 4 === 2 ? 'Veicolo' : 'Attrezzatura',
  lastMaintenance: '2024-01-01',
  nextMaintenance: '2025-01-01',
  status: i % 10 === 0 ? 'In manutenzione' : 'Operativo',
  responsible: i % 3 === 0 ? 'Alessio' : i % 3 === 1 ? 'Lorenzo' : 'Gian Franco',
  repeatability: i % 4 === 0 ? '3 mesi' : i % 4 === 1 ? '6 mesi' : i % 4 === 2 ? '1 anno' : '2 anni'
}));
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'name',
  label: 'Macchinario'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'lastMaintenance',
  label: 'Ultima manut.'
}, {
  key: 'nextMaintenance',
  label: 'Prox. manut.'
}, {
  key: 'responsible',
  label: 'Responsabile'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function EquipmentPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [maintenanceData, setMaintenanceData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialEquipmentData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'type', 'code']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    // Pre-fill code
    const nextCode = `EQP-${String(initialEquipmentData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      sendEmailReminder: true
    });
    setIsModalOpen(true);
  };
  const handleMaintenance = (item: any) => {
    setSelectedItem(item);
    setMaintenanceData({
      date: new Date().toISOString().split('T')[0],
      type: 'Ordinaria',
      sendEmailReminder: true
    });
    setIsMaintenanceModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Macchinario salvato', 'Il macchinario è stato salvato con successo.');
    }, 1500);
  };
  const saveMaintenance = () => {
    addNotification('success', 'Manutenzione registrata', 'La manutenzione è stata salvata con successo.');
    setIsMaintenanceModalOpen(false);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-2013">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2014">
        <div data-id="element-2015">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2016">
            Macchinari e manutenzione
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2017">
            Gestione asset aziendali e manutenzioni programmate.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2018">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2020" />} data-id="element-2019">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2022" />} data-id="element-2021">
            Nuovo macchinario
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-2023">
        <div className="p-4 flex flex-col gap-4" data-id="element-2024">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2025">
            <div className="flex-1" data-id="element-2026">
              <Input placeholder="Cerca macchinario..." startContent={<Search className="h-4 w-4" data-id="element-2028" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2027" />
            </div>
            <div className="flex gap-2" data-id="element-2029">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2031" />} data-id="element-2030">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2032">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="equipment" data-id="element-2033" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2034">
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Operativo',
            label: 'Operativo'
          }, {
            value: 'In manutenzione',
            label: 'In manutenzione'
          }]} data-id="element-2035" />
              <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Strumento Misura',
            label: 'Strumento Misura'
          }, {
            value: 'Hardware',
            label: 'Hardware'
          }, {
            value: 'Veicolo',
            label: 'Veicolo'
          }, {
            value: 'Attrezzatura',
            label: 'Attrezzatura'
          }]} data-id="element-2036" />
              <div className="col-span-full flex justify-end" data-id="element-2037">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2039" />} data-id="element-2038">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-2040">
        <div className="overflow-x-auto" data-id="element-2041">
          <table className="w-full text-left border-collapse table-fixed" data-id="element-2042">
            <thead data-id="element-2043">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2044">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-28' : ''}`} data-id="element-2045">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2046">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2047">
                  {visibleColumns.includes('code') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-2048">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('name') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2049">
                      {item.name}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2050">
                      {item.type}
                    </td>}
                  {visibleColumns.includes('lastMaintenance') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2051">
                      {item.lastMaintenance}
                    </td>}
                  {visibleColumns.includes('nextMaintenance') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2052">
                      {item.nextMaintenance}
                    </td>}
                  {visibleColumns.includes('responsible') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2053">
                      {item.responsible}
                    </td>}
                  {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-2054">
                      <Badge variant={item.status === 'Operativo' ? 'success' : 'warning'} data-id="element-2055">
                        {item.status}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" data-id="element-2056">
                      <div className="flex items-center justify-end gap-2" data-id="element-2057">
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleMaintenance(item)} title="Registra manutenzione" data-id="element-2058">
                          <Wrench className="h-4 w-4 text-slate-500" data-id="element-2059" />
                        </Button>
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-2060">
                          <Edit className="h-4 w-4 text-seform-orange" data-id="element-2061" />
                        </Button>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-2062">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2063" />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica macchinario' : 'Nuovo macchinario'} size="2xl" data-id="element-2064">
        <AnimatePresence mode="wait" data-id="element-2065">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2066">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2067">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2068" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2069">
                Macchinario salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2070">
              <div className="space-y-8" data-id="element-2071">
                <section data-id="element-2072">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2073">
                    <Wrench className="h-4 w-4" data-id="element-2074" /> Dettagli macchinario
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2075">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} placeholder="EQP-001" data-id="element-2076" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Strumento Misura',
                  label: 'Strumento Misura'
                }, {
                  value: 'Hardware',
                  label: 'Hardware'
                }, {
                  value: 'Veicolo',
                  label: 'Veicolo'
                }, {
                  value: 'Attrezzatura',
                  label: 'Attrezzatura'
                }]} required data-id="element-2077" />
                    <Input label="Nome" placeholder="Es. Fonometro" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required className="col-span-full" data-id="element-2078" />
                    <div className="col-span-full" data-id="element-2079">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} data-id="element-2080" />
                    </div>
                    <Input label="Responsabile" placeholder="Nome responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-2081" />
                    <Select label="Cliente (opzionale)" value={formData.client || ''} onChange={e => setFormData({
                  ...formData,
                  client: e.target.value
                })} options={[{
                  value: '',
                  label: 'Nessuno'
                }, {
                  value: 'Azienda 1',
                  label: 'Azienda 1'
                }, {
                  value: 'Azienda 2',
                  label: 'Azienda 2'
                }]} data-id="element-2082" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-2083"></div>

                <section data-id="element-2084">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2085">
                    <Calendar className="h-4 w-4" data-id="element-2086" /> Manutenzione programmata
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2087">
                    <DatePicker label="Prossima scadenza" value={formData.nextMaintenance || ''} onChange={e => setFormData({
                  ...formData,
                  nextMaintenance: e.target.value
                })} data-id="element-2088" />
                    <Select label="Ripetibilità" value={formData.repeatability || ''} onChange={e => setFormData({
                  ...formData,
                  repeatability: e.target.value
                })} options={[{
                  value: '3 mesi',
                  label: '3 mesi'
                }, {
                  value: '6 mesi',
                  label: '6 mesi'
                }, {
                  value: '1 anno',
                  label: '1 anno'
                }, {
                  value: '2 anni',
                  label: '2 anni'
                }]} data-id="element-2089" />
                    <div className="col-span-full flex items-center gap-2" data-id="element-2090">
                      <input type="checkbox" id="sendEmailReminder" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailReminder || false} onChange={e => setFormData({
                    ...formData,
                    sendEmailReminder: e.target.checked
                  })} data-id="element-2091" />
                      <label htmlFor="sendEmailReminder" className="text-sm text-slate-700 flex items-center gap-1" data-id="element-2092">
                        <Mail className="h-4 w-4 text-slate-400" data-id="element-2093" />
                        Invia promemoria email alla scadenza
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2094">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-2095">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2097" />} data-id="element-2096">
                    Salva macchinario
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Maintenance Modal */}
      <Modal isOpen={isMaintenanceModalOpen} onClose={() => setIsMaintenanceModalOpen(false)} title="Registra manutenzione" size="lg" data-id="element-2098">
        <div className="space-y-6" data-id="element-2099">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-4" data-id="element-2100">
            <h3 className="font-bold text-seform-navy" data-id="element-2101">{selectedItem?.name}</h3>
            <p className="text-sm text-slate-500" data-id="element-2102">
              Codice: {selectedItem?.code} • Ultima manutenzione:{' '}
              {selectedItem?.lastMaintenance}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2103">
            <DatePicker label="Data esecuzione" value={maintenanceData.date || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            date: e.target.value
          })} data-id="element-2104" />
            <Select label="Esito" value={maintenanceData.result || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            result: e.target.value
          })} options={[{
            value: 'Positivo',
            label: 'Positivo'
          }, {
            value: 'Negativo',
            label: 'Negativo'
          }]} required data-id="element-2105" />
            <Select label="Tipo manutenzione" value={maintenanceData.type || 'Ordinaria'} onChange={e => setMaintenanceData({
            ...maintenanceData,
            type: e.target.value
          })} options={[{
            value: 'Ordinaria',
            label: 'Ordinaria'
          }, {
            value: 'Straordinaria',
            label: 'Straordinaria'
          }]} required data-id="element-2106" />
            <DatePicker label="Prossima scadenza" value={maintenanceData.nextDate || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            nextDate: e.target.value
          })} data-id="element-2107" />
            <div className="col-span-full" data-id="element-2108">
              <Textarea label="Note / Interventi effettuati" value={maintenanceData.notes || ''} onChange={e => setMaintenanceData({
              ...maintenanceData,
              notes: e.target.value
            })} required data-id="element-2109" />
            </div>
            <div className="col-span-full flex items-center gap-2" data-id="element-2110">
              <input type="checkbox" id="maintenanceEmailReminder" className="rounded text-seform-orange focus:ring-seform-orange" checked={maintenanceData.sendEmailReminder || false} onChange={e => setMaintenanceData({
              ...maintenanceData,
              sendEmailReminder: e.target.checked
            })} data-id="element-2111" />
              <label htmlFor="maintenanceEmailReminder" className="text-sm text-slate-700 flex items-center gap-1" data-id="element-2112">
                <Mail className="h-4 w-4 text-slate-400" data-id="element-2113" />
                Invia promemoria email per prossima scadenza
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2114">
            <Button variant="flat" color="default" onClick={() => setIsMaintenanceModalOpen(false)} data-id="element-2115">
              Annulla
            </Button>
            <Button onClick={saveMaintenance} startContent={<CheckCircle2 className="h-4 w-4" data-id="element-2117" />} data-id="element-2116">
              Registra
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>;
}